import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const CompareContext = createContext(null);
const STORAGE_KEY = "km_compare_ids";
const MAX = 3;

export function CompareProvider({ children }) {
    const [ids, setIds] = useState([]);

    // Hydrate from localStorage
    useEffect(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (raw) {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed)) setIds(parsed.slice(0, MAX));
            }
        } catch (err) {
            console.warn("Compare: failed to hydrate from localStorage:", err);
        }
    }, []);

    // Persist on change
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
        } catch (err) {
            console.warn("Compare: failed to persist to localStorage:", err);
        }
    }, [ids]);

    const toggle = useCallback((id) => {
        setIds((cur) => {
            if (cur.includes(id)) return cur.filter((x) => x !== id);
            if (cur.length >= MAX) return [cur[1], cur[2], id].filter(Boolean);
            return [...cur, id];
        });
    }, []);

    const remove = useCallback((id) => {
        setIds((cur) => cur.filter((x) => x !== id));
    }, []);

    const clear = useCallback(() => setIds([]), []);
    const isCompared = useCallback((id) => ids.includes(id), [ids]);

    const value = useMemo(
        () => ({ ids, toggle, remove, clear, isCompared, max: MAX }),
        [ids, toggle, remove, clear, isCompared],
    );

    return <CompareContext.Provider value={value}>{children}</CompareContext.Provider>;
}

export const useCompare = () => {
    const ctx = useContext(CompareContext);
    if (!ctx) throw new Error("useCompare must be used within CompareProvider");
    return ctx;
};
